import React, { useState, useEffect } from 'react';

// Promo runs from now through May 31, 2026 (end of day)
const PROMO_END = new Date('2026-05-31T23:59:59');
// localStorage stores only a non-sensitive UI dismissal flag (no PII)
const STORAGE_KEY = 'gensol_promo_mayo_dismissed';

const calcTimeLeft = () => {
  const diff = PROMO_END.getTime() - Date.now();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const TimeUnit = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[34px] sm:min-w-[40px]">
    <span className="bg-black/40 text-white font-extrabold text-sm sm:text-base px-1.5 py-0.5 rounded-md tabular-nums leading-tight">
      {String(value).padStart(2, '0')}
    </span>
    <span className="text-[9px] sm:text-[10px] uppercase tracking-wider opacity-80 mt-0.5">
      {label}
    </span>
  </div>
);

const PromoBanner = ({ onVisibilityChange }) => {
  const [visible, setVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(calcTimeLeft());

  useEffect(() => {
    const now = new Date();
    const dismissed = localStorage.getItem(STORAGE_KEY) === '1';
    const shouldShow = now <= PROMO_END && !dismissed;
    setVisible(shouldShow);
    if (onVisibilityChange) onVisibilityChange(shouldShow);
  }, [onVisibilityChange]);

  // Tick every second while banner visible
  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => {
      const t = calcTimeLeft();
      setTimeLeft(t);
      if (!t) {
        setVisible(false);
        if (onVisibilityChange) onVisibilityChange(false);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [visible, onVisibilityChange]);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
    if (onVisibilityChange) onVisibilityChange(false);
  };

  if (!visible || !timeLeft) return null;

  return (
    <div
      data-testid="promo-banner"
      data-analytics-element="promo-banner-mayo"
      className="fixed top-0 left-0 right-0 bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 text-white py-2 px-4 shadow-md z-[60]"
    >
      <div className="container mx-auto flex items-center justify-center gap-2 sm:gap-4 text-center pr-8">
        <svg
          className="w-5 h-5 flex-shrink-0 hidden sm:block"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M12 2l2.39 7.36H22l-6.18 4.49L18.21 22 12 17.27 5.79 22l2.39-8.15L2 9.36h7.61L12 2z" />
        </svg>

        <p className="text-xs sm:text-sm md:text-base font-semibold tracking-wide">
          <span className="font-extrabold uppercase">¡Promo Mayo!</span>{' '}
          <span className="bg-white text-orange-600 font-extrabold px-2 py-0.5 rounded-md mx-1 whitespace-nowrap">
            10% DESC.
          </span>
          <span className="hidden md:inline">en generadores </span>
          <span className="font-extrabold whitespace-nowrap">20kW–100kW</span>
        </p>

        {/* Countdown */}
        <div
          data-testid="promo-banner-countdown"
          className="flex items-center gap-1 sm:gap-1.5 ml-1 sm:ml-2"
          aria-label="Tiempo restante de la promoción"
        >
          <span className="hidden md:inline text-xs uppercase tracking-wider opacity-90 mr-1">
            Termina en
          </span>
          <TimeUnit value={timeLeft.days} label="Días" />
          <span className="text-white font-bold opacity-70">:</span>
          <TimeUnit value={timeLeft.hours} label="Hrs" />
          <span className="text-white font-bold opacity-70">:</span>
          <TimeUnit value={timeLeft.minutes} label="Min" />
          <span className="text-white font-bold opacity-70 hidden sm:inline">:</span>
          <div className="hidden sm:flex">
            <TimeUnit value={timeLeft.seconds} label="Seg" />
          </div>
        </div>

        <a
          href="/contacto"
          data-testid="promo-banner-cta"
          data-analytics-click="promo-banner-cotizar"
          className="hidden lg:inline-block bg-black hover:bg-gray-900 text-white text-sm font-bold px-4 py-1.5 rounded-md transition-all hover:scale-105 ml-2 whitespace-nowrap"
        >
          Cotizar Ahora →
        </a>
      </div>

      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Cerrar promoción"
        data-testid="promo-banner-close"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/90 hover:text-white hover:bg-black/20 rounded-full p-1 transition-colors"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default PromoBanner;
